import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon, Logo, Mark, BlobField, Button } from "../components/ui";
import { familyService } from "../services/familyService";
import { useFamilyAuth } from "../store/familyAuthStore";
import { validateRequiredForm } from "../utils/validate";

const TRUST = [
  { icon: "lock", t: "One secure login per family", d: "No more re-entering your details every time." },
  { icon: "shield", t: "Private by design", d: "Nothing here touches our enquiry system — you're a Stride family, not a lead." },
  { icon: "heart", t: "Everything in one warm place", d: "Handbook, forms and your account, together." },
];

export default function Login() {
  const navigate = useNavigate();
  const setAuth = useFamilyAuth((s) => s.setAuth);
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!validateRequiredForm(e.currentTarget)) {
      setError("Please fill in all fields.");
      return;
    }

    setBusy(true);
    try {
      const res = await familyService.login(email.trim(), pw);
      if (res?.success && res?.data?.token) {
        setAuth({ token: res.data.token, family: res.data.family });
        navigate("/");
      } else {
        setError(res?.msg || "Those details didn't match. Please try again.");
      }
    } catch (err) {
      setError(err?.response?.data?.msg || "Unable to sign in right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-wrap">
      <div className="login-hero on-dark">
        <BlobField />
        <div className="login-hero-inner">
          <div className="hero-brand">
            <Mark size={52} variant="amber" />
            <Logo variant="white" height={44} />
          </div>
          <div className="hero-spacer">
            <p className="eyebrow on-dark">Family Portal</p>
            <h1 className="display hero-title">
              Welcome back to your<br />Stride family space.
            </h1>
            <div className="trust-list">
              {TRUST.map((b) => (
                <div key={b.t} className="trust-item">
                  <span className="trust-ico"><Icon name={b.icon} /></span>
                  <div>
                    <div className="trust-t">{b.t}</div>
                    <div className="trust-d">{b.d}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <p className="hero-tagline">Steps towards a bright future.</p>
        </div>
      </div>

      <div className="login-form-side">
        <form className="login-card" onSubmit={submit} noValidate>
          <p className="eyebrow">Sign in</p>
          <h2 className="auth-h2">Hello again</h2>
          <p className="auth-intro">Sign in to your family account to continue.</p>

          {error && <div className="alert-soft">{error}</div>}

          <div className="form-field">
            <label className="field-label">Email address</label>
            <input
              className="input"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              required
            />
          </div>

          <div className="form-field">
            <label className="field-label">Password</label>
            <input
              className="input"
              type="password"
              name="password"
              value={pw}
              onChange={(e) => setPw(e.target.value)}
              placeholder="Your password"
              required
            />
          </div>

          <div className="login-row">
            <label className="remember">
              <input type="checkbox" defaultChecked />
              Keep me signed in
            </label>
            <Link to="/forgot" className="nowrap">Forgot password?</Link>
          </div>

          <Button variant="primary" block type="submit" disabled={busy} iconRight={busy ? null : "chevronRight"}>
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <hr className="divider divider-lg" />
          <p className="signin-alt">
            New to the portal?{" "}
            <Link to="/register" className="link-strong">Activate your account</Link>
          </p>
        </form>
        <p className="login-foot">
          Need a hand? Call your centre and we'll help you get set up.
        </p>
      </div>
    </div>
  );
}
